<template>
  <section class="invoice-add-wrapper">
    <validation-observer ref="simpleRules">
      <b-form>
        <b-row>
          <!-- ============================================ -->
          <!-- PANEL IZQUIERDO — Datos del pedido -->
          <!-- ============================================ -->
          <b-col
            cols="12"
            md="5"
            xl="5"
          >
            <b-card
              class="order-add-wrapper"
              no-body
            >
              <b-card-body>
                <!-- Badge de estado -->
                <b-row>
                  <b-col
                    cols="12"
                    md="11"
                  >
                    <h3>
                      <b-badge
                        variant="warning"
                        pill
                      >
                        #{{ customerOrderData.Id }} - {{ customerOrderData.Estado }}
                      </b-badge>
                    </h3>
                  </b-col>
                </b-row>

                <!-- Info del pedido (read-only) -->
                <b-row class="mt-1">
                  <b-col cols="12">
                    <p class="mb-1">
                      <span class="font-weight-bold">Generado por:</span>
                      {{ customerOrderData.UsuarioNombre }} <b>el:</b> {{ customerOrderData.FechaIngresoSistema }}
                      <br>
                      <span class="font-weight-bold">Fecha del pedido:</span>
                      {{ customerOrderData.FechaPedidoCliente }}
                    </p>
                  </b-col>
                </b-row>

                <!-- Cliente (read-only) -->
                <div v-if="customerData.id">
                  <h5 class="mb-50">
                    #{{ customerData.id }} - {{ customerData.name }}
                  </h5>
                  <p class="mb-1">
                    <span class="font-weight-bold">NIT: </span>
                    {{ (customerData.nit_cf === 'Y') ? `CF - ${customerData.nit}` : customerData.nit }}
                    <br>
                    <span class="font-weight-bold">Cel: </span>{{ customerData.cellphone || '-' }}
                    <br>
                    <span class="font-weight-bold">Tel: </span>{{ customerData.phone || '-' }}
                    <br>
                    <span class="font-weight-bold">Lista de precios: </span>
                    {{ (customerOrderData.PriceListInfo && customerOrderData.PriceListInfo.name) ? customerOrderData.PriceListInfo.name : 'No asignada' }}
                  </p>

                  <!-- Dirección -->
                  <div class="mb-1">
                    <span class="font-weight-bold">Dirección: </span>
                    <p class="ml-1 mb-50">
                      {{ customerOrderData.shipping_address_text }}
                    </p>
                    <div class="ml-1">
                      <a
                        href="#"
                        @click.prevent="showAddressSelector = true"
                      >+ Ver más direcciones</a>
                    </div>
                  </div>
                </div>

                <!-- Moneda y tipo de venta -->
                <b-row>
                  <b-col
                    cols="12"
                    md="6"
                  >
                    <b-form-group>
                      <h6>
                        <b>Moneda:</b>
                      </h6>
                      <v-select
                        v-model="customerOrderData.currency_id"
                        :options="currencies"
                        label="name"
                        :reduce="currency => currency.id"
                        placeholder="Moneda"
                      >
                        <template #option="option">
                          {{ option.name }} {{ option.symbol }}
                        </template>
                      </v-select>
                    </b-form-group>
                  </b-col>

                  <b-col
                    cols="12"
                    md="6"
                  >
                    <b-form-group>
                      <h6>
                        <b>Tipo de venta:</b>
                      </h6>
                      <b-form-select
                        v-model="customerOrderData.origin_sale"
                        :options="saleOriginOptions"
                        size="sm"
                      />
                    </b-form-group>
                  </b-col>
                </b-row>

                <!-- Observaciones -->
                <b-form-group>
                  <h6>
                    <b>Observaciones:</b>
                  </h6>
                  <b-form-textarea
                    v-model="customerOrderData.observations"
                    placeholder="Comentarios del pedido"
                    rows="2"
                  />
                </b-form-group>
              </b-card-body>
            </b-card>
          </b-col>

          <!-- ============================================ -->
          <!-- PANEL DERECHO — Catálogo + Grilla -->
          <!-- ============================================ -->
          <b-col
            v-if="customerData.id"
            cols="12"
            md="7"
            xl="7"
          >
            <!-- Catálogo -->
            <b-card no-body>
              <b-card-body class="pb-50">
                <h6 class="mb-1">
                  <b>Catálogo de productos:</b>
                </h6>

                <b-form-input
                  v-model="searchQuery"
                  placeholder="Buscar por código o nombre de estilo..."
                  class="mb-1"
                />

                <div
                  v-if="!customerOrderData.price_list_id"
                  class="text-center py-2"
                >
                  <feather-icon
                    icon="AlertCircleIcon"
                    size="32"
                    class="text-warning mb-50"
                  />
                  <p class="text-muted mb-0">
                    El pedido no tiene lista de precios asignada
                  </p>
                </div>

                <div
                  v-else-if="!searchQuery.trim() && estilos.length === 0"
                  class="text-center py-2"
                >
                  <feather-icon
                    icon="SearchIcon"
                    size="32"
                    class="text-muted mb-50"
                  />
                  <p class="text-muted mb-0">
                    Busca un producto por código o nombre para agregarlo
                  </p>
                </div>

                <div
                  v-else-if="isLoadingProducts"
                  class="text-center py-2"
                >
                  <b-spinner
                    variant="primary"
                    class="mb-50"
                  />
                  <p class="text-muted mb-0">
                    Buscando productos...
                  </p>
                </div>

                <div
                  v-else-if="estilos.length === 0 && searchQuery.trim().length >= 2"
                  class="text-center py-2"
                >
                  <feather-icon
                    icon="PackageIcon"
                    size="32"
                    class="text-muted mb-50"
                  />
                  <p class="text-muted mb-0">
                    No se encontraron productos para "{{ searchQuery }}"
                  </p>
                </div>

                <div
                  v-else-if="estilos.length > 0"
                  class="order-add-wrapper__catalogo"
                >
                  <div
                    v-for="estilo in estilos"
                    :key="estilo.code + '-' + estilo.color"
                    class="estilo-card"
                    :class="{ 'estilo-card--added': isStyleAdded(estilo.code, estilo.color) }"
                    @click="onToggleStyle(estilo)"
                  >
                    <img
                      :src="getEstiloThumbnail(estilo)"
                      :alt="estilo.code"
                      class="estilo-card__thumb"
                      @error="onEstiloImageError"
                    >
                    <div class="estilo-card__info">
                      <span class="estilo-card__code">
                        {{ estilo.code }}
                      </span>
                      <small class="text-muted">
                        {{ estilo.color }}
                      </small>
                    </div>
                    <div class="estilo-card__action">
                      <feather-icon
                        v-if="isStyleAdded(estilo.code, estilo.color)"
                        icon="CheckCircleIcon"
                        size="20"
                        class="text-success"
                      />
                      <feather-icon
                        v-else
                        icon="PlusCircleIcon"
                        size="20"
                        class="text-primary"
                      />
                    </div>
                  </div>
                </div>
              </b-card-body>
            </b-card>

            <!-- Grilla del pedido -->
            <b-card no-body>
              <b-card-body>
                <h6 class="mb-1">
                  <b>Detalle del pedido ({{ activeLines.length }} productos):</b>
                </h6>

                <div class="order-grid">
                  <div
                    v-if="gridRows.length === 0"
                    class="text-center py-2"
                  >
                    <feather-icon
                      icon="ShoppingCartIcon"
                      size="32"
                      class="text-muted mb-1"
                    />
                    <p class="text-muted mb-0">
                      El pedido no tiene productos
                    </p>
                  </div>

                  <div
                    v-else
                    class="table-responsive"
                  >
                    <table class="table table-sm order-grid__table">
                      <thead>
                        <tr>
                          <th class="order-grid__th-style">
                            Estilo-Color
                          </th>
                          <th
                            v-for="size in allSizes"
                            :key="size"
                            class="order-grid__th-size"
                          >
                            {{ size }}
                          </th>
                          <th class="order-grid__th-num">
                            TOTAL
                          </th>
                          <th class="order-grid__th-price">
                            Precio unit
                          </th>
                          <th class="order-grid__th-num">
                            MONEDA
                          </th>
                          <th class="order-grid__th-amount">
                            TOTAL EN {{ currencySymbol }}
                          </th>
                          <th class="order-grid__th-actions" />
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="row in gridRows"
                          :key="row.key"
                        >
                          <td class="order-grid__style-cell">
                            <span class="font-weight-bold">
                              {{ row.estilo }}
                            </span>
                            <small class="text-muted d-block">
                              {{ row.color }}
                            </small>
                          </td>
                          <td
                            v-for="size in allSizes"
                            :key="size"
                            class="order-grid__qty-cell"
                          >
                            <template v-if="row.sizeMap[size]">
                              <input
                                type="number"
                                class="order-grid__qty-input"
                                :class="{
                                  'order-grid__qty-input--max': row.sizeMap[size].stock !== null && row.sizeMap[size].cantidad >= effectiveMax(row.sizeMap[size]) && effectiveMax(row.sizeMap[size]) > 0,
                                  'order-grid__qty-input--empty': row.sizeMap[size].cantidad === 0,
                                  'order-grid__qty-input--no-stock': row.sizeMap[size].stock === 0 && (row.sizeMap[size].cantidadOriginal || 0) === 0,
                                }"
                                :value="row.sizeMap[size].cantidad || ''"
                                placeholder="0"
                                min="0"
                                :max="row.sizeMap[size].stock !== null ? effectiveMax(row.sizeMap[size]) : undefined"
                                :disabled="row.sizeMap[size].stock === 0 && (row.sizeMap[size].cantidadOriginal || 0) === 0"
                                @change="onQtyChange(row.sizeMap[size], $event)"
                              >
                              <small
                                v-if="row.sizeMap[size].stock !== null"
                                class="order-grid__stock-hint"
                              >
                                {{ row.sizeMap[size].stock }}
                              </small>
                              <!-- Talla con oferta: precio de lista tachado y precio ofertado. -->
                              <div
                                v-if="lineOffer(row.sizeMap[size])"
                                class="order-grid__offer"
                                :title="`Oferta: ahorra ${currencySymbol}${lineOffer(row.sizeMap[size]).ahorro.toFixed(2)} por unidad`"
                              >
                                <s>{{ currencySymbol }}{{ lineOffer(row.sizeMap[size]).lista.toFixed(2) }}</s>
                                <strong>{{ currencySymbol }}{{ lineOffer(row.sizeMap[size]).oferta.toFixed(2) }}</strong>
                              </div>
                            </template>
                          </td>
                          <td class="text-center font-weight-bold">
                            {{ row.totalQty }}
                          </td>
                          <td>
                            <div class="price-input">
                              <span class="price-input__display price-input__display--locked">
                                {{ currencySymbol }}{{ Number(row.precioActual).toFixed(2) }}
                              </span>
                              <span
                                v-if="row.isCustomPrice"
                                class="price-input__badge"
                                title="Precio propio de la lista; no sigue al precio base del estilo"
                              >
                                lista
                              </span>
                              <span
                                v-else
                                class="price-input__original"
                                title="Esta variante sigue el precio base del estilo"
                              >
                                base
                              </span>
                            </div>
                          </td>
                          <td class="text-center text-muted">
                            {{ currencyCode }}
                          </td>
                          <td class="text-right font-weight-bold order-grid__row-total">
                            {{ currencySymbol }}{{ row.totalAmount }}
                          </td>
                          <td class="text-center">
                            <button
                              type="button"
                              class="btn btn-sm btn-flat-danger"
                              @click="onRemoveStyle(row.key)"
                            >
                              <feather-icon
                                icon="TrashIcon"
                                size="14"
                              />
                            </button>
                          </td>
                        </tr>
                      </tbody>
                    </table>

                    <div class="order-grid__footer">
                      <span>Total:</span>
                      <span class="order-grid__grand-total">
                        {{ currencySymbol }}{{ grandTotal }}
                      </span>
                    </div>
                  </div>
                </div>

                <!-- Minimo de unidades de la lista (PRECIO VOLUMEN); el servidor tambien bloquea. -->
                <price-list-unit-rule-notice
                  :customer-id="customerOrderData.customer_id"
                  :price-list-id="customerOrderData.price_list_id"
                  :units="unitRuleUnits"
                  documento="pedido"
                  :order-id="orderId"
                  @change="unitRuleOk = $event"
                />

                <b-button
                  v-ripple.400="'rgba(255, 159, 67, 0.15)'"
                  variant="warning"
                  block
                  class="mt-1"
                  :disabled="isSavingOrder || activeLines.length === 0 || !unitRuleOk"
                  @click="updateData"
                >
                  <template v-if="isSavingOrder">
                    <b-spinner
                      small
                      class="mr-50"
                    />
                    Actualizando...
                  </template>
                  <template v-else>
                    Actualizar pedido
                  </template>
                </b-button>
              </b-card-body>
            </b-card>
          </b-col>
        </b-row>

        <customers-address-selector
          :show.sync="showAddressSelector"
          :customer-id="customerOrderData.customer_id"
          :selected-address-id="customerOrderData.shipping_address_id"
          @address-selected="selectAddress"
        />
      </b-form>
    </validation-observer>
  </section>
</template>

<script>
import axios from '@axios'
import { ValidationObserver } from 'vee-validate'
import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'
import { buildStorageUrl, NO_IMAGE_URL } from '@/utils/urls'
import { lineOffer, rowPriceSummary, catalogVariant } from '../components/gridOffer'
import { orderUnits } from '../components/unitRuleStatus'
import PriceListUnitRuleNotice from '../components/PriceListUnitRuleNotice.vue'
import CustomersAddressSelector from '../../contacts/customers/CustomersAddressSelector.vue'

export default {
  components: {
    PriceListUnitRuleNotice,
    vSelect,
    ValidationObserver,
    CustomersAddressSelector,
  },
  directives: {
    Ripple,
  },

  props: {
    orderId: {
      type: Number,
      default: () => 0,
    },
  },

  data() {
    return {
      customerOrderData: {
        Id: 0,
        Estado: '',
        FechaIngresoSistema: '',
        FechaPedidoCliente: '',
        UsuarioNombre: '',
        customer_id: 0,
        shipping_address_id: null,
        shipping_address_text: '',
        origin_sale: 'Cliente Final',
        observations: '',
        currency_id: null,
        price_list_id: null,
        PriceListInfo: null,
        CurrencyInfo: null,
      },

      customerData: {},
      currencies: [],

      allVariants: [],
      estilos: [],
      searchQuery: '',
      isLoadingProducts: false,

      orderLines: [],

      saleOriginOptions: [
        { value: 'Cliente Final', text: 'Cliente Final' },
        { value: 'Venta en línea', text: 'Venta en línea' },
      ],

      showAddressSelector: false,

      isSavingOrder: false,
      // Lo informa PriceListUnitRuleNotice: false bloquea el boton de guardar.
      unitRuleOk: true,
      timeout: null,

    }
  },

  computed: {
    // Unidades para el minimo de la lista: en cotizacion, las cubiertas por
    // stock; en pedido, las del pedido (ver unitRuleStatus.js).
    unitRuleUnits() {
      return orderUnits(this.activeLines)
    },

    activeLines() {
      return this.orderLines.filter(line => line.cantidad > 0)
    },

    currencySymbol() {
      if (!this.customerOrderData.currency_id) return 'Q'
      const selected = this.currencies.find(c => c.id === this.customerOrderData.currency_id)
      return selected ? (selected.symbol || 'Q') : 'Q'
    },

    currencyCode() {
      if (!this.customerOrderData.currency_id) return 'GTQ'
      const selected = this.currencies.find(c => c.id === this.customerOrderData.currency_id)
      return selected && selected.code ? selected.code : 'GTQ'
    },

    allSizes() {
      const sizes = new Set()
      this.orderLines.forEach(line => sizes.add(line.size))
      const arr = [...sizes]
      arr.sort((a, b) => {
        const numA = parseFloat(a)
        const numB = parseFloat(b)
        if (!Number.isNaN(numA) && !Number.isNaN(numB)) return numA - numB
        return a.localeCompare(b)
      })
      return arr
    },

    gridRows() {
      const groups = {}

      this.orderLines.forEach(line => {
        const key = `${line.estilo}-${line.color}`
        if (!groups[key]) {
          groups[key] = {
            key,
            estilo: line.estilo,
            color: line.color,
            precioActual: line.precioActual,
            precioBase: line.precioBase,
            isCustomPrice: line.isCustomPrice,
            sizeMap: {},
            lines: [],
          }
        }
        groups[key].sizeMap[line.size] = line
        groups[key].lines.push(line)
      })

      return Object.values(groups).map(group => ({
        ...group,
        ...rowPriceSummary(group.lines),
        totalQty: group.lines.reduce((sum, l) => sum + l.cantidad, 0),
        totalAmount: Number(
          group.lines.reduce((sum, l) => sum + (l.precioActual * l.cantidad), 0),
        ).toFixed(2),
      }))
    },

    grandTotal() {
      return Number(
        this.orderLines.reduce((sum, l) => sum + (l.precioActual * l.cantidad), 0),
      ).toFixed(2)
    },
  },

  watch: {
    searchQuery(val) {
      if (this.timeout) clearTimeout(this.timeout)
      const query = val.trim()
      if (query.length < 2) {
        if (query.length === 0) {
          this.allVariants = []
          this.estilos = []
        }
        return
      }
      this.timeout = setTimeout(() => {
        this.searchProductsFromServer()
      }, 400)
    },
  },

  mounted() {
    if (this.orderId > 0) {
      this.getCustomerOrderData()
      this.getCurrencies()
    }
  },

  methods: {
    lineOffer,

    isStyleAdded(code, color) {
      return this.orderLines.some(l => l.estilo === code && l.color === color)
    },

    getCustomerOrderData() {
      axios
        .get(`customer-order/${this.orderId}`)
        .then(response => {
          const d = response.data.data
          this.customerOrderData.Id = d.Id
          this.customerOrderData.Estado = d.Estado
          this.customerOrderData.FechaIngresoSistema = d.FechaIngresoSistema
          this.customerOrderData.FechaPedidoCliente = d.FechaPedidoCliente
          this.customerOrderData.UsuarioNombre = d.UsuarioNombre
          this.customerOrderData.observations = d.Observaciones || ''
          this.customerOrderData.origin_sale = d.OriginSale || 'Cliente Final'
          this.customerOrderData.shipping_address_id = d.DireccionEnvioId
          this.customerOrderData.shipping_address_text = d.DireccionEnvio
          this.customerOrderData.PriceListInfo = d.PriceListInfo
          this.customerOrderData.price_list_id = d.PriceListInfo ? d.PriceListInfo.id : null
          this.customerOrderData.currency_id = d.CurrencyId
          this.customerOrderData.CurrencyInfo = d.CurrencyInfo

          this.customerData = d.CustomerInfo || {}
          this.customerOrderData.customer_id = d.CustomerInfo ? d.CustomerInfo.id : 0

          this.orderLines = []
          d.Detalle.forEach(el => {
            const basePrice = parseFloat(el.precio_original)
            const currentPrice = parseFloat(el.precio_pedido)
            const listPrice = el.precio_lista !== undefined ? parseFloat(el.precio_lista) : currentPrice
            this.orderLines.push({
              sku: el.sku,
              estilo: el.producto,
              color: el.producto_color,
              descripcion: el.producto_desc,
              precioBase: basePrice,
              precioActual: currentPrice,
              cantidad: el.cantidad,
              cantidadOriginal: el.cantidad,
              productVariantId: el.idproductos,
              presentacion: el.producto_presentacion,
              size: el.producto_talla,
              imagen: el.producto_imagen,
              isCustomPrice: basePrice !== currentPrice,
              precioLista: listPrice,
              isOffer: listPrice > currentPrice,
              stock: null,
            })
          })

          this.loadAllSizesForOrderStyles()
        })
        .catch(error => { this.showErrors(error) })
    },

    loadAllSizesForOrderStyles() {
      if (!this.customerOrderData.price_list_id || !this.customerOrderData.customer_id) return
      if (this.orderLines.length === 0) return

      const styleMap = {}
      this.orderLines.forEach(l => {
        const key = `${l.estilo}-${l.color}`
        if (!styleMap[key]) {
          styleMap[key] = {
            estilo: l.estilo,
            color: l.color,
            precioActual: l.precioActual,
            precioBase: l.precioBase,
          }
        }
      })

      const styles = Object.values(styleMap)

      Promise.all(styles.map(s => axios.get('product-variant-with-prices', {
        params: {
          price_list_id: this.customerOrderData.price_list_id,
          customer_id: this.customerOrderData.customer_id,
          search: s.estilo,
          perPage: 100,
        },
      })))
        .then(responses => {
          responses.forEach((resp, idx) => {
            const s = styles[idx]
            const variants = resp.data.data.data || []
            variants.forEach(v => {
              if (v.code !== s.estilo) return
              if (v.color !== s.color) return

              const existingIdx = this.orderLines.findIndex(l => l.sku === v.sku)
              if (existingIdx !== -1) {
                this.$set(this.orderLines, existingIdx, {
                  ...this.orderLines[existingIdx],
                  stock: v.stock,
                })
                return
              }

              this.orderLines.push({
                sku: v.sku,
                estilo: s.estilo,
                color: s.color,
                descripcion: v.descripcion || '',
                precioBase: v.base_price,
                precioActual: v.precio,
                precioLista: v.precio_lista,
                isOffer: v.is_offer,
                cantidad: 0,
                cantidadOriginal: 0,
                productVariantId: v.id,
                presentacion: v.presentacion,
                size: v.size,
                imagen: v.imagen,
                isCustomPrice: v.is_custom_price,
                stock: v.stock,
              })
            })
          })
        })
        .catch(error => { this.showErrors(error) })
    },

    getCurrencies() {
      axios.get('currency-active')
        .then(response => { this.currencies = response.data.data })
        .catch(error => { this.showErrors(error) })
    },

    searchProductsFromServer() {
      if (!this.customerOrderData.price_list_id || !this.customerOrderData.customer_id) return

      const query = this.searchQuery.trim()
      if (query.length < 2) return

      this.isLoadingProducts = true

      const params = {
        price_list_id: this.customerOrderData.price_list_id,
        customer_id: this.customerOrderData.customer_id,
        search: query,
        perPage: 50,
      }

      axios.get('product-variant-with-prices', { params })
        .then(response => {
          this.allVariants = response.data.data.data || []
          this.groupByEstilo()
          this.isLoadingProducts = false
        })
        .catch(error => {
          this.isLoadingProducts = false
          this.showErrors(error)
        })
    },

    groupByEstilo() {
      const map = {}

      this.allVariants.forEach(v => {
        const code = v.code || v.label.split('-').slice(0, -1).join('-') || v.label
        const key = `${code}-${v.color}`

        if (!map[key]) {
          map[key] = {
            code,
            description: v.descripcion || '',
            color: v.color || '',
            image: v.imagen || null,
            variants: [],
          }
        }

        map[key].variants.push(catalogVariant(v))
      })

      Object.values(map).forEach(estilo => {
        estilo.variants.sort((a, b) => {
          const numA = parseFloat(a.size)
          const numB = parseFloat(b.size)
          if (!Number.isNaN(numA) && !Number.isNaN(numB)) return numA - numB
          return a.size.localeCompare(b.size)
        })
      })

      this.estilos = Object.values(map)
    },

    onToggleStyle(estilo) {
      // Cada talla con su propio precio (lista + oferta vigente); ya no se
      // hereda el precio de otra talla del mismo estilo.
      estilo.variants.forEach(v => {
        const exists = this.orderLines.some(l => l.sku === v.sku)
        if (exists) return

        const precioActual = v.precio
        this.orderLines.push({
          sku: v.sku,
          estilo: estilo.code,
          color: estilo.color,
          descripcion: v.descripcion || estilo.description,
          precioBase: v.base_price,
          precioActual,
          cantidad: 0,
          cantidadOriginal: 0,
          productVariantId: v.id,
          presentacion: v.presentacion,
          size: v.size,
          imagen: estilo.image,
          isCustomPrice: v.is_custom_price,
          precioLista: v.precio_lista,
          isOffer: v.is_offer,
          stock: v.stock,
        })
      })
    },

    onRemoveStyle(styleKey) {
      this.orderLines = this.orderLines.filter(
        line => `${line.estilo}-${line.color}` !== styleKey,
      )
    },

    onQtyChange(line, event) {
      const { target } = event
      const val = parseInt(target.value, 10)
      let qty
      if (Number.isNaN(val) || val < 0) {
        target.value = 0
        qty = 0
      } else {
        const max = this.effectiveMax(line)
        qty = max !== null ? Math.min(val, max) : val
        target.value = qty || ''
      }
      const stored = this.orderLines.find(l => l.sku === line.sku)
      if (stored) stored.cantidad = qty
    },

    effectiveMax(line) {
      if (!line || line.stock === null || line.stock === undefined) return null
      return line.stock + (line.cantidadOriginal || 0)
    },

    getEstiloThumbnail(estilo) {
      if (estilo.image && estilo.image.path) {
        return buildStorageUrl(estilo.image.path)
      }
      return NO_IMAGE_URL
    },

    onEstiloImageError(e) {
      e.target.src = NO_IMAGE_URL
    },

    selectAddress(address) {
      if (!address) return
      this.customerOrderData.shipping_address_id = address.id
      this.customerOrderData.shipping_address_text = `${address.name}: ${address.address} - ${address.municipality} / ${address.department} ${address.reference ? `/ Referencia: ${address.reference}` : ''}`
    },

    updateData() {
      if (this.activeLines.length === 0) {
        this.makeToast('warning', 'Alerta', 'El pedido debe tener al menos un producto con cantidad mayor a 0.')
        return
      }

      if (!this.customerOrderData.currency_id) {
        this.makeToast('warning', 'Alerta', 'Debe seleccionar una moneda.')
        return
      }

      this.$bvModal
        .msgBoxConfirm('¿Deseas actualizar el pedido?', {
          title: 'Confirmar',
          size: 'sm',
          okVariant: 'warning',
          cancelVariant: 'outline-dark',
          okTitle: 'SI',
          cancelTitle: 'NO',
          hideHeaderClose: false,
          centered: true,
        })
        .then(value => {
          if (!value) return

          this.isSavingOrder = true

          const payload = {
            Id: this.customerOrderData.Id,
            Estado: this.customerOrderData.Estado,
            customer_id: this.customerOrderData.customer_id,
            shipping_address_id: this.customerOrderData.shipping_address_id,
            shipping_address_text: this.customerOrderData.shipping_address_text,
            origin_sale: this.customerOrderData.origin_sale,
            observations: this.customerOrderData.observations,
            currency_id: this.customerOrderData.currency_id,
            products: this.activeLines.map(line => ({
              product_id: line.productVariantId,
              label: line.estilo,
              color: line.color,
              presentacion: line.presentacion,
              size: line.size,
              description: line.descripcion,
              price: line.precioActual,
              quantity: line.cantidad,
              imagen: line.imagen,
            })),
          }

          axios
            .put(`customer-order/${this.orderId}`, payload)
            .then(async response => {
              if (response.data.res) {
                this.makeToast('success', 'Pedido actualizado', `Se ha actualizado el pedido #${response.data.data.Id} correctamente.`)
                this.isSavingOrder = false
                await new Promise(resolve => { window.setTimeout(resolve, 500) })
                this.$emit('addOrderTab', parseInt(response.data.data.Id, 10))
              } else {
                throw response
              }
            })
            .catch(error => {
              this.isSavingOrder = false
              this.showErrors(error)
            })
        })
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

.order-add-wrapper {
  .add-new-customer-header {
    padding: $options-padding-y $options-padding-x;
    color: $success;

    &:hover {
      background-color: rgba($success, 0.12);
    }
  }

  &__catalogo {
    max-height: 300px;
    overflow-y: auto;
    padding-right: 4px;
  }
}

.estilo-card {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  margin-bottom: 8px;
  background: #fff;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #fafafa;
  }

  &--added {
    border-color: #28c76f;
    background: #f0faf4;
  }

  &__thumb {
    width: 40px;
    height: 40px;
    border-radius: 6px;
    object-fit: cover;
    border: 1px solid #d8dee9;
    flex-shrink: 0;
  }

  &__info {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  &__code {
    font-weight: 700;
    font-size: 13px;
    color: #1a3a5c;
  }

  &__action {
    flex-shrink: 0;
  }
}

.price-input {
  display: flex;
  align-items: center;
  gap: 6px;

  &__display {
    font-weight: 600;
    padding: 2px 6px;
    border-radius: 4px;

    &--locked {
      cursor: default;
      color: #1a3a5c;
    }
  }

  &__original {
    font-size: 10px;
    color: #b9b9c3;
    text-transform: uppercase;
  }

  &__badge {
    font-size: 10px;
    background: #fff3e8;
    color: #ff9f43;
    padding: 1px 6px;
    border-radius: 10px;
    font-weight: 500;
  }

  &__field {
    width: 90px;
    padding: 2px 6px;
    border: 1px solid #1a3a5c;
    border-radius: 4px;
    font-size: 14px;
    outline: none;

    &:focus {
      box-shadow: 0 0 0 2px rgba(26, 58, 92, 0.2);
    }
  }
}

.order-grid {
  &__table {
    font-size: 12px;
    white-space: nowrap;
    margin-bottom: 0;

    th {
      font-size: 10px;
      text-transform: uppercase;
      color: #6e6b7b;
      font-weight: 600;
      padding: 6px 4px;
      text-align: center;
      background: #f8f9fa;
      border-bottom: 2px solid #1a3a5c;
    }

    td {
      vertical-align: middle;
      padding: 4px;
      border-bottom: 1px solid #ebe9f1;
    }
  }

  &__th-style {
    min-width: 120px;
    text-align: left !important;
  }

  &__th-size {
    min-width: 48px;
  }

  &__th-num {
    min-width: 55px;
  }

  &__th-price {
    min-width: 110px;
  }

  &__th-amount {
    min-width: 110px;
    text-align: right !important;
  }

  &__th-actions {
    width: 40px;
  }

  &__style-cell {
    background: #fafbfc;
    border-right: 1px solid #ebe9f1;
    padding-left: 8px !important;
  }

  &__qty-cell {
    text-align: center;
  }

  &__qty-input {
    width: 40px;
    height: 28px;
    text-align: center;
    border: 1px solid #d8dee9;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 600;
    padding: 0;
    background: #fff;
    -moz-appearance: textfield;

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    &:focus {
      outline: none;
      border-color: #1a3a5c;
      box-shadow: 0 0 0 2px rgba(26, 58, 92, 0.15);
    }

    &--empty {
      background: #fafbfc;
      color: #b9b9c3;
      border-style: dashed;
    }

    &--max {
      border-color: #ff9f43;
      background: #fff8f0;
    }

    &--no-stock {
      background: #f5f5f5;
      color: #ccc;
      cursor: not-allowed;
      border-color: #e8e8e8;
    }
  }

  // Talla con oferta: precio de lista tachado y ofertado debajo de la cantidad.
  &__offer {
    display: flex;
    flex-direction: column;
    align-items: center;
    line-height: 1.1;
    margin-top: 2px;
    font-size: 0.75rem;

    s {
      color: #82868b;
    }

    strong {
      color: #ea5455;
    }
  }

  &__stock-hint {
    display: block;
    font-size: 9px;
    color: #b9b9c3;
    margin-top: 1px;
  }

  &__row-total {
    white-space: nowrap;
  }

  &__footer {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 12px;
    padding: 12px 8px;
    border-top: 2px solid #1a3a5c;
    font-size: 16px;
    font-weight: 600;
  }

  &__grand-total {
    color: #1a3a5c;
    font-size: 20px;
  }
}
</style>
